import { MutationCache, QueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/client";
import { describeApiError } from "@/api/errors";
import { useUiStore } from "@/store/ui";

/**
 * `QueryClient` 的**唯一构造点**（含"变更失败必须出声"的公共兜底）。
 *
 * ## 为什么要有这个文件
 *
 * `RULES §11.2` 的后半句是硬规则：**变更失败 → 必须提示，不得乐观更新为成功态**
 * （CONSTITUTION D1）。而在此之前的实测（2026-09-29 第三十八轮）是：
 * 全仓 **29 处 `.mutate(`** 里只有 **8 处**自带了失败提示——`ClassifyView` 的
 * `resolve` / `save` / `approve`、`ReviewView` 的 `approve` / `reject`、
 * `GenerateView` 的 `retry` / `cancel`、`SimulateView` 的 `rerun`、
 * `ArtifactDetail` 的 `submit` / `transition` / `stale` 等**失败后什么都不说**：
 * 用户点了"通过关口"、点了"重试运行"，界面毫无反应（按钮从 pending 回到常态）。
 *
 * 于是把兜底放到**唯一一个所有变更都必经的地方**：`MutationCache.onError`。
 * 默认**出声**——新增一处变更即使什么都没写，失败也会被说出来；
 * 忘了声明只会多一条 toast（响、可见），**不会退回静默**。
 *
 * ## 与站点自带的失败提示怎么共存（**实测过的语义，不要凭直觉改**）
 *
 * ⚠️ TanStack Query 把各级 `onError` **合并**而不是覆盖：探针实测（同轮）
 * `defaultOptions` / hook 级 / per-call 三处若都写了 `onError`，**三个都会跑**。
 * 所以"站点自己接了就不重复"这件事**必须由站点声明**，不能由框架判断：
 *
 * | 声明 | 放在哪 | 作用 |
 * | --- | --- | --- |
 * | `meta: { errorHandled: true }` | **`useMutation({ ... })`**（hook 级） | 该 hook 的失败已由站点就地呈现，公共兜底**跳过** |
 * | `meta: { errorTitle: "提交失败" }` | hook 级 | 兜底 toast 用这个标题（不给则用「操作未完成」） |
 *
 * ⚠️ **`meta` 只有 hook 级生效**：同一轮探针实测，`.mutate(vars, { meta })` 里的
 * per-call `meta` 在 `MutationCache.onError` 里读到的是 `null`——所以声明只能写在
 * `api/queries.ts` 的 hook 定义上。推论是一条纪律：
 * **给某个 hook 声明 `errorHandled` 之前，必须先确认它的每一个调用点都已自呈现**
 * （否则那个调用点会重新变成静默——本轮逐点核对过 14 个 hook 的全部调用点）。
 *
 * ## 盲区（人工评审项）
 *
 * 就地呈现"够不够"（一句 `inline-note` 算不算提示）由人判断，机检修不了；
 * 本轮把"算"的都登记为 `errorHandled`，判据是**同一屏幕上失败信息可见**。
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        const meta = (mutation.meta ?? {}) as { errorHandled?: boolean; errorTitle?: string };
        if (meta.errorHandled) return;

        const view = describeApiError(error);
        useUiStore.getState().pushToast({
          title: meta.errorTitle ?? "操作未完成",
          detail: view.action ? `${view.message} ${view.action}` : view.message,
          mark: "!",
        });
      },
    }),
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => {
          // 4xx 是业务错误，重试没有意义；只有网络类错误才重试。
          if (error instanceof ApiError && error.status < 500) return false;
          return failureCount < 1;
        },
        refetchOnWindowFocus: false,
        staleTime: 30_000,
      },
    },
  });
}
